import React, { FC, useState } from "react";
import { headerSty, textSty } from "../UIElements/styles-typography";
import useResponsive from "../../hooks/useResponsive";
import { useTheme } from "@mui/material/styles";
import { Box, Button, Stack, Typography } from "@mui/material";
import { labelStyles } from "../UIElements/styles-input";
import { CandidateDetailsProps } from "../../interface/application";
import {
  extractContractDates,
  formatDateAndTimeFromString,
  formatDateFromString,
  formatTextType,
} from "../../utils/url-id";
import { Link } from "react-router-dom";
import EmployeeForm from "./EmployeeForm";
import { useNavigate } from "react-router-dom";
import DocumentsDisplay from "../UIElements/document-display";
import { useMutation } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import { apiUpdateApplicationStatus } from "../../api/application";
import { LoadingButton } from "@mui/lab";
import RejectCandidateModal, { ScheduleInterviewModal } from "./JobBoardModal";
import MedicHistory from "./MedicHistory";

const CandidateDetails: FC<{ candidate_info: CandidateDetailsProps }> = ({
  candidate_info,
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const [employ, setEmploy] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotificationService();
  const headerStyles = {
    ...headerSty,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };

  // Check if candidate is already employed
  const isEmployed = candidate_info?.status.toLowerCase() === "accepted";

  const mutation = useMutation({
    mutationFn: (fieldValues: { status: string; applicationId: string }) =>
      apiUpdateApplicationStatus(fieldValues),
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        navigate(-1);
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      // Handle error
      showNotification("Process failed", "error");
    },
  });

  const rejectHandler = (val: string) => {
    const field = {
      status: "rejected",
      applicationId: candidate_info._id,
    };
    mutation.mutate(field);
  };

  return (
    <Box sx={{}}>
      {/*  */}
      {isEmployed ? (
        <Box
          sx={{
            my: 2,
            p: 3,
            backgroundColor: theme.palette.success.light,
            borderRadius: 2,
            border: `2px solid ${theme.palette.success.main}`,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.success.dark,
              fontWeight: 700,
              fontSize: "1.1rem",
              textAlign: "center",
            }}
          >
            This candidate has already been accepted
          </Typography>
        </Box>
      ) : (
        <Stack
          direction={"row"}
          spacing={3}
          justifyContent={"flex-end"}
          sx={{ my: 2 }}
        >
          {candidate_info?.interview?.link && (
            <LoadingButton
              onClick={() => {
                window.open(candidate_info.interview.link, "_blank");
              }}
              variant="contained"
              loading={mutation.isPending}
              color="success"
              sx={{
                borderRadius: 1,
                fontWeight: 700,
                backgroundColor: "#0A81D1",
                color: "#ffff",
              }}
            >
              Go to Interview
            </LoadingButton>
          )}

          <LoadingButton
            onClick={() => setScheduleModal(!scheduleModal)}
            variant="contained"
            loading={mutation.isPending}
            color="primary"
            sx={{
              borderRadius: 1,
              fontWeight: 700,
              color: "#fff",
              backgroundColor: "#003566",
              "&:hover": {
                backgroundColor: "#222",
              },
            }}
          >
            {candidate_info?.interview
              ? "Reschedule Interview"
              : "Schedule Interview"}
          </LoadingButton>

          <LoadingButton
            onClick={() => setRejectModal(!rejectModal)}
            variant="contained"
            loading={mutation.isPending}
            // color="error"
            sx={{
              borderRadius: 1,
              fontWeight: 700,
              backgroundColor: "#D72638",
            }}
          >
            Reject Candidate
          </LoadingButton>
          <Button
            onClick={() => setEmploy(!employ)}
            variant="contained"
            sx={{
              borderRadius: 1,
              fontWeight: 700,
              backgroundColor: "#284CB1",
              "&:hover": {
                backgroundColor: "#0FA3B1",
              },
            }}
          >
            {employ ? "Back" : "Employ Candidate"}
          </Button>
        </Stack>
      )}
      <RejectCandidateModal
        open={rejectModal}
        modalHandler={() => setRejectModal(!rejectModal)}
        rejectHandler={rejectHandler}
      />
      <ScheduleInterviewModal
        open={scheduleModal}
        modalHandler={() => setScheduleModal(!scheduleModal)}
        candidateId={candidate_info._id}
        refresh={() => {}}
      />
      {!employ ? (
        <>
          <Box>
            <Typography
              sx={{ ...headerStyles, my: 0, mb: 2 }}
              variant="subtitle1"
            >
              Medic Information
            </Typography>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Full Name
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {candidate_info?.medic_info?.name ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Medic Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.medic_info?.medic_type ?? "")}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"flex-start"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Gender:</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.medic_info?.gender) ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Date of Birth:
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatDateFromString(candidate_info?.medic_info?.dob) ?? "-"}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"flex-start"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <DocumentsDisplay
                documentsUrl={candidate_info?.documents_url}
                labelStyles={labelStyles}
                textSty={textSty}
              />
            </Stack>
          </Box>
          <Box>
            <Typography
              sx={{ ...headerStyles, my: 0, mb: 2 }}
              variant="subtitle2"
            >
              Job Information
            </Typography>

            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Title
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {candidate_info?.job_info?.job_title}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Type:
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.job_info?.job_type)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Department
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {candidate_info?.job_info?.department}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Status
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.job_info?.status)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.job_info?.work_type)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Location
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.job_info?.work_location)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Employment Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.job_info?.employment_type) ??
                    "-"}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Description
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.job_info?.job_description)}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Application Deadline
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatDateFromString(
                    candidate_info?.job_info?.application_deadline,
                  ) ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Status</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.job_info?.status) ?? "-"}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              {!!candidate_info?.job_info?.contract_duration &&
                candidate_info?.job_info?.job_type === "temporary" && (
                  <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                    <Typography sx={{ ...labelStyles, mb: 0 }}>
                      Contract Duration
                    </Typography>
                    <Typography sx={{ ...textSty }}>
                      {extractContractDates(
                        candidate_info?.job_info?.contract_duration,
                      ) ?? "-"}
                    </Typography>
                  </Stack>
                )}
            </Stack>
          </Box>
          <Box>
            <Typography
              sx={{ ...headerStyles, my: 0, mb: 2 }}
              variant="subtitle2"
            >
              Facility Information
            </Typography>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Facility Name
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {candidate_info?.job_info?.facility_info?.name ?? "-"}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Box>
            <Typography
              sx={{ ...headerStyles, my: 0, mb: 2 }}
              variant="subtitle2"
            >
              Interview Information
            </Typography>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Meeting Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(candidate_info?.interview?.type)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Date/Time
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatDateAndTimeFromString(
                    candidate_info?.interview?.interview_date,
                  ) ?? "-"}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Details</Typography>
                <Typography sx={{ ...textSty }}>
                  {candidate_info?.interview?.details ?? "-"}
                </Typography>
              </Stack>

              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  {candidate_info?.interview?.type === "virtual"
                    ? "Virtual"
                    : "Physical"}
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {candidate_info?.interview?.type === "virtual" ? (
                    <Link to={candidate_info?.interview?.link} target="_blank">
                      Meeting
                    </Link>
                  ) : (
                    candidate_info?.interview?.location
                  )}
                </Typography>
              </Stack>
            </Stack>

            <Box sx={{ my: 4 }}>
              <Typography
                sx={{ ...headerStyles, my: 0, mb: 2 }}
                variant="subtitle1"
              >
                Medic History
              </Typography>
              <MedicHistory medic_id={candidate_info?._id} />
            </Box>
          </Box>
        </>
      ) : (
        <EmployeeForm
          candidate_info={candidate_info}
          onCancel={() => setEmploy(false)}
        />
      )}
    </Box>
  );
};

export default CandidateDetails;
